import React, { useEffect, useState } from "react";
import AdminSectionHeader from "@/components/admin/AdminSectionHeader";
import { api, formatApiError } from "@/lib/api";
import CTAButton from "@/components/app/CTAButton";
import { useTheme } from "@/contexts/ThemeContext";
import { Save, RefreshCw } from "lucide-react";

const FIELDS = [
    ["background", "Fond"],
    ["surface", "Surface"],
    ["surface_elevated", "Surface élevée"],
    ["primary", "Primary"],
    ["secondary", "Secondary (or)"],
    ["accent_success", "Accent succès"],
    ["warning", "Warning"],
    ["danger", "Danger"],
    ["text_primary", "Texte principal"],
    ["text_secondary", "Texte secondaire"],
    ["border", "Bordure"],
];

const RARITY_FIELDS = [
    ["counco", "Counco"],
    ["rare", "Rare"],
    ["holo", "Holo"],
    ["reverse", "Reverse"],
    ["ex_v", "EX / V"],
];

function ColorField({ label, value, onChange, testId }) {
    return (
        <label className="flex items-center gap-3 rounded-lg border border-md-border bg-md-bg px-3 py-2">
            <input
                type="color"
                value={value || "#000000"}
                onChange={(e) => onChange(e.target.value)}
                className="h-7 w-9 cursor-pointer rounded border border-md-border bg-transparent p-0"
                data-testid={testId}
            />
            <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-md-text">{label}</p>
                <p className="font-mono text-[10px] uppercase text-md-text-2">{value}</p>
            </div>
        </label>
    );
}

export default function AdminBrandTheme() {
    const [theme, setTheme] = useState(null);
    const [draft, setDraft] = useState(null);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const { applyTheme: applyPreview, setTheme: setCtxTheme } = useTheme();

    useEffect(() => {
        api.get("/admin/brand-theme")
            .then(({ data }) => { setTheme(data); setDraft(data); })
            .catch((e) => setError(formatApiError(e)));
    }, []);

    function setMode(mode, key, val) {
        setDraft((d) => ({ ...d, [mode]: { ...(d[mode] || {}), [key]: val } }));
    }
    function setRarity(key, val) {
        setDraft((d) => ({ ...d, rarity_colors: { ...(d.rarity_colors || {}), [key]: val } }));
    }

    function preview() {
        if (draft) {
            applyPreview(draft);
            setCtxTheme(draft);
        }
    }

    async function save() {
        if (!draft) return;
        setSaving(true);
        setError("");
        try {
            const payload = {
                name: draft.name,
                light: draft.light,
                dark: draft.dark,
                rarity_colors: draft.rarity_colors,
            };
            await api.put("/admin/brand-theme", payload);
            setTheme(draft);
            setCtxTheme(draft);
            applyPreview(draft);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    function reset() {
        if (theme) {
            setDraft(theme);
            applyPreview(theme);
            setCtxTheme(theme);
        }
    }

    if (!draft) {
        return <div className="text-sm text-md-text-2">Chargement du thème…</div>;
    }

    return (
        <>
            <AdminSectionHeader
                title="Brand Theme"
                description="Toute la palette de Mission Dresseur. Aucune couleur n’est codée en dur dans l’UI."
                action={
                    <div className="flex gap-2">
                        <CTAButton variant="outline" size="sm" onClick={reset} icon={<RefreshCw className="h-3.5 w-3.5" />} testId="theme-reset">
                            Réinitialiser
                        </CTAButton>
                        <CTAButton variant="outline" size="sm" onClick={preview} testId="theme-preview">
                            Aperçu live
                        </CTAButton>
                        <CTAButton size="sm" loading={saving} onClick={save} icon={<Save className="h-3.5 w-3.5" />} testId="theme-save">
                            Enregistrer
                        </CTAButton>
                    </div>
                }
            />
            {error && <div className="mb-4 rounded-lg border border-md-danger/30 bg-md-danger/10 px-4 py-2 text-sm text-md-danger">{error}</div>}

            <div className="mb-4">
                <label className="md-overline">Nom du thème</label>
                <input
                    type="text" value={draft.name || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    data-testid="theme-name"
                    className="mt-1 block w-full max-w-md rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm text-md-text outline-none focus:border-md-primary"
                />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-md-border bg-md-surface p-6">
                    <p className="md-overline">Palette Light</p>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {FIELDS.map(([k, label]) => (
                            <ColorField
                                key={`l-${k}`}
                                label={label}
                                value={draft.light?.[k]}
                                onChange={(v) => setMode("light", k, v)}
                                testId={`theme-light-${k}`}
                            />
                        ))}
                    </div>
                </div>
                <div className="rounded-2xl border border-md-border bg-md-surface p-6">
                    <p className="md-overline">Palette Dark</p>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {FIELDS.map(([k, label]) => (
                            <ColorField
                                key={`d-${k}`}
                                label={label}
                                value={draft.dark?.[k]}
                                onChange={(v) => setMode("dark", k, v)}
                                testId={`theme-dark-${k}`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-6 rounded-2xl border border-md-border bg-md-surface p-6">
                <p className="md-overline">Couleurs de rareté</p>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                    {RARITY_FIELDS.map(([k, label]) => (
                        <ColorField
                            key={`r-${k}`}
                            label={label}
                            value={draft.rarity_colors?.[k]}
                            onChange={(v) => setRarity(k, v)}
                            testId={`theme-rarity-${k}`}
                        />
                    ))}
                </div>
            </div>
        </>
    );
}
